import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Users, ShieldCheck, Truck, Target, Award, Phone, MessageCircle, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { businessInfo } from "../data/mock";

const values = [
  {
    icon: ShieldCheck,
    title: "Trust & Reliability",
    description: "We are committed to delivering every shipment safely and on time, building trust with each delivery.",
  },
  {
    icon: Users,
    title: "Customer First",
    description: "Our customers are at the heart of everything we do. We strive to exceed expectations with every interaction.",
  },
  {
    icon: Target,
    title: "Precision & Care",
    description: "Every package is handled with the utmost care and precision, from pickup to final delivery.",
  },
  {
    icon: Award,
    title: "Quality Service",
    description: "We maintain high standards of service quality, partnering with India's best courier companies.",
  },
];

const milestones = [
  "Established as a local courier service provider in New Delhi",
  "Partnered with Trackon for nationwide delivery coverage",
  "Expanded services to include express and time-critical delivery",
  "Partnered with Shree Maruti for enhanced surface logistics",
  "Integrated with Delhivery for technology-driven logistics solutions",
  "Serving 10,000+ happy customers across 500+ cities",
];

export default function AboutPage() {
  return (
    <div>
      {/* Page Header */}
      <section className="bg-brand-dark py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-brand-light uppercase tracking-wider mb-2">About Us</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: "'Lora', serif" }}>About Om Sai Express</h1>
          <p className="text-white/70 mt-3 max-w-2xl text-base">Your trusted partner for courier and logistics solutions in New Delhi and across India.</p>
        </div>
      </section>

      {/* Company Story */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Lora', serif" }}>Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Om Sai Express was founded with a simple mission — to provide reliable, safe, and affordable courier services to the people of New Delhi and beyond. What started as a small local courier service has grown into a trusted logistics partner serving thousands of customers.
                </p>
                <p>
                  Based in Mansarovar Garden, New Delhi, we have built strong partnerships with some of India's leading courier companies including Trackon, Shree Maruti, and Delhivery. These partnerships allow us to offer our customers the best routes, rates, and delivery timelines.
                </p>
                <p>
                  Whether you need to send a single document or manage regular commercial shipments, Om Sai Express is here to make your delivery experience smooth, hassle-free, and dependable.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1620455800201-7f00aeef12ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxkZWxpdmVyeSUyMHRydWNrfGVufDB8fHx8MTc3MDg3NDk5Mnww&ixlib=rb-4.1.0&q=85&w=600"
                alt="Courier Service Operations"
                className="rounded-xl shadow-lg w-full h-auto object-cover max-h-[380px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-brand uppercase tracking-wider mb-2">Our Values</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>What Drives Us</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="text-center bg-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-full bg-brand-lightest flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-6 h-6 text-brand" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Lora', serif" }}>{value.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-brand uppercase tracking-wider mb-2">Our Journey</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>Key Milestones</h2>
            </div>
            <div className="space-y-4">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed pt-1">{milestone}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand py-14 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Lora', serif" }}>Want to Know More?</h2>
          <p className="text-white/80 mb-8">Get in touch with us for any queries or to schedule a pickup.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-brand-dark hover:bg-gray-100 font-semibold shadow-lg h-12 px-8">
              <Link to="/contact">
                <Package className="w-5 h-5" />
                Contact Us
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#1da851] text-white font-semibold shadow-lg h-12 px-8">
              <a href={businessInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-5 h-5" />
                WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
